import { useState, useEffect, useCallback } from 'react';

// Type declaration for window.ENV is in app/types/window.d.ts

export interface PushSubscriptionState {
  isSupported: boolean;
  permission: NotificationPermission | null;
  subscription: PushSubscription | null;
  isLoading: boolean;
  error: string | null;
}

function getVapidPublicKey(): string | undefined {
  if (typeof window !== 'undefined') {
    return window.ENV?.VAPID_PUBLIC_KEY;
  }
  return undefined;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    permission: null,
    subscription: null,
    isLoading: true,
    error: null,
  });

  // Check initial support and permission state
  useEffect(() => {
    const checkSupport = async () => {
      try {
        const isSupported =
          'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

        if (!isSupported) {
          setState(prev => ({
            ...prev,
            isSupported: false,
            isLoading: false,
            error:
              'Push notifications are not supported in this browser. Required APIs: Service Worker, Push Manager, and Notifications.',
          }));
          return;
        }

        // Check if service worker is ready before trying to access push manager
        if ('serviceWorker' in navigator) {
          // First check if any service workers are registered
          const registrations = await navigator.serviceWorker.getRegistrations();

          if (registrations.length === 0) {
            try {
              await navigator.serviceWorker.register('/sw.js');
              await navigator.serviceWorker.ready;
            } catch (swError) {
              throw new Error(`Service worker registration failed: ${swError}`);
            }
          }

          const registration = await navigator.serviceWorker.ready;

          if (registration) {
            const subscription = await registration.pushManager.getSubscription();

            setState(prev => ({
              ...prev,
              isSupported: true,
              permission: Notification.permission,
              subscription,
              isLoading: false,
            }));
          } else {
            throw new Error('Service worker registration failed');
          }
        } else {
          throw new Error('Service worker not available');
        }
      } catch (error) {
        setState(prev => ({
          ...prev,
          isSupported: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to initialize push notifications',
        }));
      }
    };

    checkSupport();
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      setState(prev => ({ ...prev, error: 'Push notifications not supported' }));
      return false;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        setState(prev => ({
          ...prev,
          permission,
          isLoading: false,
          error: 'Permission denied for push notifications',
        }));
        return false;
      }

      setState(prev => ({ ...prev, permission, isLoading: false }));
      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to request permission',
      }));
      return false;
    }
  }, [state.isSupported]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported || state.permission !== 'granted') {
      return false;
    }

    const vapidPublicKey = getVapidPublicKey();
    if (!vapidPublicKey) {
      setState(prev => ({ ...prev, error: 'VAPID public key not configured' }));
      return false;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      // Send subscription to server
      const response = await fetch('/api/push-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscription.toJSON()),
      });

      if (!response.ok) {
        throw new Error('Failed to store subscription');
      }

      setState(prev => ({
        ...prev,
        subscription,
        isLoading: false,
      }));

      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to subscribe',
      }));
      return false;
    }
  }, [state.isSupported, state.permission]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!state.subscription) {
      return true;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      await state.subscription.unsubscribe();

      setState(prev => ({
        ...prev,
        subscription: null,
        isLoading: false,
      }));

      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to unsubscribe',
      }));
      return false;
    }
  }, [state.subscription]);

  return {
    ...state,
    requestPermission,
    subscribe,
    unsubscribe,
  };
}
